import { useState } from "react";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { FiGlobe, FiZap, FiTrash2 } from "react-icons/fi";
import Button from "@/components/loading-button";
import useStore from "@/services/store";
import api from "@/services/api";

import CultureCreate from "./create";
import CultureResult from "./result";

const translations = {
  en: {
    no_dilemmas_title: "No culture dilemmas yet",
    no_dilemmas_description: "Generate personalized culture dilemmas based on your company and industry.",
    generate_dilemmas: "Generate Dilemmas",
    delete_dilemmas: "Delete",
    deleted: "Culture dilemmas deleted",
  },
  fr: {
    no_dilemmas_title: "Pas encore de dilemmes culturels",
    no_dilemmas_description: "Generez des dilemmes culturels personnalises bases sur votre entreprise et votre industrie.",
    generate_dilemmas: "Generer les dilemmes",
    delete_dilemmas: "Supprimer",
    deleted: "Dilemmes culturels supprimes",
  },
};

export default function Culture() {
  const { t } = useComponentTranslation(translations);
  const { organization, setOrganization } = useStore();
  const [generating, setGenerating] = useState(false);

  const dilemmas = organization?.culture_generated_dilemmas || [];
  const hasResults = organization?.culture_profile?.status === "done";
  const hasDilemmas = dilemmas.length === 6;
  const mode = !organization ? "loading" : hasResults ? "results" : hasDilemmas ? "wizard" : "empty";

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { ok, data } = await api.post("/organization/culture-dilemmas/generate", { organization_id: organization._id });
      if (!ok) throw new Error("Failed to generate dilemmas");
      setOrganization(data);
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { ok, data } = await api.put("/organization", {
        organization_id: organization._id,
        culture_generated_dilemmas: [],
        culture_dilemmas: [],
        culture_profile: null,
      });
      if (!ok) throw new Error("Failed to delete");
      setOrganization(data);
      toast.success(t("deleted"));
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    }
  };

  if (mode === "loading") return null;

  if (mode === "empty") {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center max-w-md">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <FiGlobe className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t("no_dilemmas_title")}</h2>
          <p className="text-sm text-gray-500 mb-6">{t("no_dilemmas_description")}</p>
          <Button loading={generating} onClick={handleGenerate} className="btn btn-primary flex items-center gap-2 mx-auto">
            <FiZap className="w-4 h-4" /> {t("generate_dilemmas")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-6">
        <button type="button" onClick={handleDelete} className="flex items-center gap-2 btn btn-small text-red-600 border border-red-200 hover:bg-red-50">
          <FiTrash2 className="w-4 h-4" /> {t("delete_dilemmas")}
        </button>
      </div>
      {mode === "wizard" ? <CultureCreate onDone={setOrganization} /> : <CultureResult />}
    </div>
  );
}
